import { Handshake, History } from '@mui/icons-material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { GridColDef } from '@mui/x-data-grid-pro';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { t } from 'i18next';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { CriticalityBadge } from '../../components/Badges/CriticalityBadge';
import { CriticalityScoreBadge } from '../../components/Badges/CriticalityScoreBadge';
import { PropagationBadge } from '../../components/Badges/PropagationBadge';
import { ServiceCategoryBadge } from '../../components/Badges/ServiceCategoryBadge';
import { CountBadge } from '../../components/CountBadge';
import DatagridWrapper, { DataGridId } from '../../components/DatagridWrapper';
import InfoButton from '../../components/InfoButton';
import InfoDrawer from '../../components/InfoDrawer';
import { OrganisationContext } from '../../components/Layout/OrganisationPicker';
import GetLastElementsInList from '../../components/Logic/ListLogic';
import { OpenInNewTabButton } from '../../components/OpenInNewTabButton';
import getEnvironment from '../../env';
import { PaginationQueryPackage } from '../../models/API/QueryParams/PaginationQueryPackage';
import { ServiceCategoryEnum } from '../../models/enums/ServiceCategoryEnum';
import { ServiceBase, SimpleServiceResponse } from '../../models/Service';
import { ServiceTagResponse } from '../../models/ServiceTag';
import { GetManyPackage } from '../../redux/GetManyPackage';
import { NotCorrectRights } from '../Permission/NotCorrectRights';
import { PermissionEnum } from '../Permission/PermissionEnum';
import { AbilityContext, Can } from '../UserApi/logic/Can';
import { ServiceInfoDialog } from './serviceInfoDialog/ServiceInfoDialog';
import { compareIpList } from '../../components/Logic/IPComparator';

const getCurrentService = (services: SimpleServiceResponse[], params: GridRenderCellParams<any, any, any>): SimpleServiceResponse => {
    const service = services.find(x => x.uuid === params.id)!;
    return service;
}
class ServiceListProps {
    getServices?: UseQuery<QueryDefinition<GetManyPackage, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>, any, SimpleServiceResponse[], any>>
    services?: SimpleServiceResponse[]
    setCheckedServices?: Dispatch<SetStateAction<SimpleServiceResponse[]>>
    maxSelected?: number
    checkedServices?: SimpleServiceResponse[]
    idToFetch?: string
    disableDrawer?: boolean
    extendedActions?: (service: SimpleServiceResponse) => JSX.Element
    simpleView?: boolean = false
}
export const ServiceList = (props: ServiceListProps) => {
    const ability = useContext(AbilityContext);
    const organisationContext = useContext(OrganisationContext)?.organisation;

    const canReadList = ability.can(PermissionEnum.SERVICE_READ, organisationContext)
    if (!canReadList)
        return <NotCorrectRights />

    return (<ServiceListContent {...props} />)
}

const ServiceListContent = (props: ServiceListProps) => {
    const [clickedRow, setClickedRow] = useState<SimpleServiceResponse | undefined>(undefined)

    const services = getServices(props);

    const serviceTags = [...new Set(services?.map(service => service.servicetag?.name ?? ""))]
        .filter(tag => tag !== "").sort()

    const getColumns = (services: SimpleServiceResponse[]) => {

        const simpleView: GridColDef[] = [
            { field: "short_id", sortable: true, headerName: t('ID'), type: "number", renderCell: (params) => <div>{params.row.short_id}</div>, flex: 1 },
            { field: "name", sortable: true, minWidth: 150, headerName: t('Name'), type: "string", flex: 3 },
            { field: "criticality_score", sortable: true, minWidth: 100, headerName: t('Criticality score'), renderCell: (params) => <CriticalityScoreBadge ressource={params.row as ServiceBase} />, type: "number", flex: 1.5 },
            {
                field: "service_categories", sortable: true,
                renderCell: (params) => <>{params.row.service_categories.map((category: ServiceCategoryEnum, index: number) => <><ServiceCategoryBadge asText key={category} ressource={category} />{index + 1 != params.row.service_categories.length && <>, </>} </>)}</>,
                headerName: t('Categories'),
                type: "string",
                valueGetter: (service_categories?: ServiceCategoryEnum[]) => service_categories?.map(category => t(category)),
                flex: 2
            },
        ];

        const fullView: GridColDef[] = [
            { field: "criticality", sortable: true, headerName: t('Criticality'), renderCell: (params) => <CriticalityBadge ressource={params.row.criticality} />, type: "string", flex: 2 },
            { field: "propagation", sortable: true, headerName: t('Propagation'), renderCell: (params) => <PropagationBadge ressource={params.row.propagation} />, type: "string", flex: 2 },
            { field: "owner_organisation", sortable: true, headerName: t('Owner organisation'), type: "string", valueGetter: (_, row: SimpleServiceResponse) => row.owner_organisation?.name, flex: 2 },
            { field: 'active_agreements', renderCell: (params) => <CountBadge prefix={t("agreements")} icon={Handshake} count={params.row.active_agreements} />, sortable: true, type: "number", headerName: t('Agreements'), flex: 1 },
            { field: "servicetag", sortable: true, headerName: t('Servicegroup'), type: "singleSelect", valueOptions: serviceTags, valueGetter: (serviceTag?: ServiceTagResponse) => serviceTag?.name, flex: 2 },
            { field: "ips", sortable: true, headerName: t('IP addresses'), type: "string", valueGetter: (_, row: SimpleServiceResponse) => [...new Set(row.endpoints?.map(endpoint => endpoint.ip))], flex: 2, sortComparator: compareIpList },
            { field: "portalias", sortable: true, headerName: t('Portalias'), type: "string", valueGetter: (_, row: SimpleServiceResponse) => [...new Set(row.endpoints?.flatMap<string>(endpoint => endpoint.portaliases))].sort().toString(), flex: 2 },
            { field: "dns_names", sortable: false, headerName: t('DNS names'), type: "string", valueGetter: (_, row: SimpleServiceResponse) => [...new Set(row.endpoints?.map(endpoint => endpoint.dns_name + "." + endpoint.dns_suffix?.dns_suffix + "." + getEnvironment().REACT_APP_DSDN_DOMAIN))], flex: 2 },
        ];

        const buttonsView: GridColDef[] = [
            {
                field: 'events',
                sortable: false,
                hideable: false,
                valueGetter: () => "",
                headerName: t('Actions'),
                type: "string",
                minWidth: props.simpleView ? 100 : 220,
                renderCell: (params => {
                    const service = getCurrentService(services, params)
                    return (props.simpleView ?
                        <OpenInNewTabButton url={`/services/${service.uuid}`} />
                        :
                        <>
                            {!props.disableDrawer &&
                                <InfoButton onClick={() => setClickedRow(service)} />
                            }

                            <Can I={PermissionEnum.AUDIT_READ} this={service}>
                                <OpenInNewTabButton tooltipText='Events' icon={<History />} url={`/events?entity_uuid=${service.uuid}`} />
                            </Can>
                            <OpenInNewTabButton url={`/services/${service.uuid}`} />
                            {props.extendedActions && props.extendedActions(service)}
                        </>
                    )
                })
            }
        ]
        if (props.simpleView) {
            return simpleView.concat(buttonsView);
        } else {
            return simpleView.concat(fullView).concat(buttonsView)
        }

    }

    return (
        <>
            <DatagridWrapper
                dataGridId={props.simpleView ? DataGridId.SERVICES_SIMPLE : DataGridId.SERVICES}
                getRowId={(row) => row.uuid}
                autoHeight
                onRowClick={(row) => {
                    {
                        if (!props.disableDrawer) {
                            setClickedRow(row.row);
                        }
                    }
                }}
                loading={services == undefined}
                rows={services ?? []}
                columns={getColumns(services ?? [])}
                onRowSelectionModelChange={(selected) => {
                    if (props.setCheckedServices && services) {
                        const newCheckedServices = GetLastElementsInList(selected, props.maxSelected).map(uuid => services?.find(service => service.uuid === uuid));
                        props.setCheckedServices(newCheckedServices.filter((item): item is SimpleServiceResponse => !!item))
                    }
                }}
                rowSelectionModel={props.checkedServices?.map(x => x.uuid)}
                checkboxSelection={props.checkedServices != undefined}
                initialSortModel={[{ field: 'servicetag', sort: 'asc' }]}
            />

            <InfoDrawer
                open={clickedRow != undefined}
                onClose={() => setClickedRow(undefined)}
                entityUuid={clickedRow?.uuid}>
                {clickedRow &&
                    <ServiceInfoDialog
                        key={clickedRow.uuid}
                        service={clickedRow}
                        afterDeleteAction={() => setClickedRow(undefined)} />}
            </InfoDrawer>
        </>
    )

    function getServices(props: ServiceListProps) {
        if (props.services != undefined)
            return props.services


        const currentResult = props.getServices?.({
            pagination: new PaginationQueryPackage(),
            uuid: props.idToFetch
        });

        return currentResult?.data
    }
}

